import { lazy, Suspense } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import { CustomerAuthProvider } from "./contexts/CustomerAuthContext";
import { CorporatePermissionProvider } from "./hooks/useCorporatePermissions";
import { PMAuthProvider } from "./contexts/PMAuthContext";
import { BaaSAuthProvider } from "./contexts/BaaSAuthContext";

// Always-static: tiny pages / layouts loaded on first paint
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import VerifyIdentityPage from "./pages/VerifyIdentityPage";
import PMVerifyEndUser from "./pages/pm/PMVerifyEndUser";
import PDFPreview from "./pages/PDFPreview";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminMFASetup from "./pages/admin/AdminMFASetup";
import AdminMFAChallenge from "./pages/admin/AdminMFAChallenge";
import AdminSetPassword from "./pages/admin/AdminSetPassword";
import CustomerLogin from "./pages/customer/CustomerLogin";
import CustomerLayout from "./pages/customer/CustomerLayout";
import CustomerMFASetup from "./pages/customer/CustomerMFASetup";
import CustomerMFAChallenge from "./pages/customer/CustomerMFAChallenge";
import CustomerResetPassword from "./pages/customer/CustomerResetPassword";
import DeprecatedRoute from "./components/admin/DeprecatedRoute";
const JoinInvitation = lazy(() => import("./pages/customer/JoinInvitation"));

// ─── BaaS Portal ──────────────────────────────────────────────────────────────
import BaaSLayout from "./pages/baas/BaaSLayout";
import BaaSLogin from "./pages/baas/BaaSLogin";
const BaaSSetPassword = lazy(() => import("./pages/baas/BaaSSetPassword"));
const BaaSMFASetup = lazy(() => import("./pages/baas/BaaSMFASetup"));
const BaaSMFAVerify = lazy(() => import("./pages/baas/BaaSMFAVerify"));
const BaaSDashboard = lazy(() => import("./pages/baas/BaaSDashboard"));
const BaaSCustomersPage = lazy(() => import("./pages/baas/BaaSCustomersPage"));
const BaaSNewCustomerPage = lazy(() => import("./pages/baas/BaaSNewCustomerPage"));
const BaaSKYCPage = lazy(() => import("./pages/baas/BaaSKYCPage"));
const BaaSAccountsPage = lazy(() => import("./pages/baas/BaaSAccountsPage"));
const BaaSAlertsPage = lazy(() => import("./pages/baas/BaaSAlertsPage"));
const BaaSExportsPage = lazy(() => import("./pages/baas/BaaSExportsPage"));
const BaaSAuditPage = lazy(() => import("./pages/baas/BaaSAuditPage"));
const BaaSSettingsPage = lazy(() => import("./pages/baas/BaaSSettingsPage"));
const BaaSManualOpsPage = lazy(() => import("./pages/baas/BaaSManualOpsPage"));
const BaaSSandboxPage = lazy(() => import("./pages/baas/BaaSSandboxPage"));
const BaaSStatusPage = lazy(() => import("./pages/baas/BaaSStatusPage"));
const BaaSSafeguardingPage = lazy(() => import("./pages/baas/BaaSSafeguardingPage"));
const BaaSSupportPage = lazy(() => import("./pages/baas/BaaSSupportPage"));
const BaaSBillingPage = lazy(() => import("./pages/baas/BaaSBillingPage"));
const BaaSPaymentsPage = lazy(() => import("./pages/baas/BaaSPaymentsPage"));
const BaaSRequestsPage = lazy(() => import("./pages/baas/BaaSRequestsPage"));
const BaaSCasesPage = lazy(() => import("./pages/baas/BaaSCasesPage"));

// ─── PM Portal ────────────────────────────────────────────────────────────────
import PMLayout from "./pages/pm/PMLayout";
import PMLogin from "./pages/pm/PMLogin";
const PMMFASetup = lazy(() => import("./pages/pm/PMMFASetup"));
const PMMFAVerify = lazy(() => import("./pages/pm/PMMFAVerify"));
const PMCommandCenterPage = lazy(() => import("./pages/pm/PMCommandCenterPage"));
const PMDashboard = lazy(() => import("./pages/pm/PMDashboard"));
const PMAccessDenied = lazy(() => import("./pages/pm/PMAccessDenied"));
const PMCardsPage = lazy(() => import("./pages/pm/PMCardsPage"));
const PMCardDetailPage = lazy(() => import("./pages/pm/PMCardDetailPage"));
const PMTransactionsPage = lazy(() => import("./pages/pm/PMTransactionsPage"));
const PMDisputesPage = lazy(() => import("./pages/pm/PMDisputesPage"));
const PMEndUsersPage = lazy(() => import("./pages/pm/PMEndUsersPage"));
const PMOnboardingStatusPage = lazy(() => import("./pages/pm/PMOnboardingStatusPage"));
const PMWebhookAuditPage = lazy(() => import("./pages/pm/PMWebhookAuditPage"));
const PMEndUserDetailPage = lazy(() => import("./pages/pm/PMEndUserDetailPage"));
const PMFraudAlertsPage = lazy(() => import("./pages/pm/PMFraudAlertsPage"));
const PMCompliancePage = lazy(() => import("./pages/pm/PMCompliancePage"));
const PMReportsPage = lazy(() => import("./pages/pm/PMReportsPage"));
const PMOrdersPage = lazy(() => import("./pages/pm/PMOrdersPage"));
const PMControlsPage = lazy(() => import("./pages/pm/PMControlsPage"));
const PMApiPage = lazy(() => import("./pages/pm/PMApiPage"));
const PMBillingPage = lazy(() => import("./pages/pm/PMBillingPage"));
const PMTeamPage = lazy(() => import("./pages/pm/PMTeamPage"));
const PMAuditPage = lazy(() => import("./pages/pm/PMAuditPage"));
const PMSettingsPage = lazy(() => import("./pages/pm/PMSettingsPage"));
const PMApprovalsPage = lazy(() => import("./pages/pm/PMApprovalsPage"));
const PMScheduledReportsPage = lazy(() => import("./pages/pm/PMScheduledReportsPage"));
const PMAnalyticsPage = lazy(() => import("./pages/pm/PMAnalyticsPage"));
const PMRequestsPage = lazy(() => import("./pages/pm/PMRequestsPage"));
const PMBrandingPage = lazy(() => import("./pages/pm/PMBrandingPage"));
const PMWebhooksPage = lazy(() => import("./pages/pm/PMWebhooksPage"));
const PMSettlementPage = lazy(() => import("./pages/pm/PMSettlementPage"));
const PMStatementsPage = lazy(() => import("./pages/pm/PMStatementsPage"));

// ─── Customer pages ───────────────────────────────────────────────────────────
const CustomerHome = lazy(() => import("./pages/customer/CustomerHome"));
const AccountsList = lazy(() => import("./pages/customer/AccountsList"));
const AccountDetail = lazy(() => import("./pages/customer/AccountDetail"));
const HeldAmountsPage = lazy(() => import("./pages/customer/HeldAmountsPage"));
const OpenAccountPage = lazy(() => import("./pages/customer/OpenAccountPage"));
const BeneficiariesPage = lazy(() => import("./pages/customer/BeneficiariesPage"));
const PaymentsPage = lazy(() => import("./pages/customer/PaymentsPage"));
const InternalTransferPage = lazy(() => import("./pages/customer/InternalTransferPage"));
const PaymentRequestsPage = lazy(() => import("./pages/customer/PaymentRequestsPage"));
const ExchangePage = lazy(() => import("./pages/customer/ExchangePage"));
const CardsPage = lazy(() => import("./pages/customer/CardsPage"));
const CardDetailPage = lazy(() => import("./pages/customer/CardDetailPage"));
const CardBenefitsPage = lazy(() => import("./pages/customer/CardBenefitsPage"));
const CardOrderPage = lazy(() => import("./pages/customer/CardOrderPage"));
const PlanPage = lazy(() => import("./pages/customer/PlanPage"));
const PlanBenefitsPage = lazy(() => import("./pages/customer/PlanBenefitsPage"));
const CashbackPage = lazy(() => import("./pages/customer/CashbackPage"));
const SettingsPage = lazy(() => import("./pages/customer/SettingsPage"));
const PersonalDetailsPage = lazy(() => import("./pages/customer/PersonalDetailsPage"));
const SessionsPage = lazy(() => import("./pages/customer/SessionsPage"));
const SupportPage = lazy(() => import("./pages/customer/SupportPage"));
const SupportTicketPage = lazy(() => import("./pages/customer/SupportTicketPage"));
const ClientRequestsPage = lazy(() => import("./pages/customer/ClientRequestsPage"));
const ThreeDSChallengePage = lazy(() => import("./pages/customer/ThreeDSChallengePage"));
const CorporateTeamPage = lazy(() => import("./pages/customer/CorporateTeamPage"));
const CorporateUsersPage = lazy(() => import("./pages/customer/CorporateUsersPage"));
const FeesPage = lazy(() => import("./pages/customer/FeesPage"));
const StatementsPage = lazy(() => import("./pages/customer/StatementsPage"));
const AppearancePage = lazy(() => import("./pages/customer/AppearancePage"));
const AvatarPage = lazy(() => import("./pages/customer/AvatarPage"));
const RemindersPage = lazy(() => import("./pages/customer/RemindersPage"));
const VerificationStatusPage = lazy(() => import("./pages/customer/VerificationStatusPage"));
const DocumentsPage = lazy(() => import("./pages/customer/DocumentsPage"));
const NotificationsSettingsPage = lazy(() => import("./pages/customer/NotificationsSettingsPage"));
const ChatPage = lazy(() => import("./pages/customer/ChatPage"));
const AccountingIntegrationsPage = lazy(() => import("./pages/customer/AccountingIntegrationsPage"));
const AccountingCallbackPage = lazy(() => import("./pages/customer/AccountingCallbackPage"));
const IntegrationsPage = lazy(() => import("./pages/customer/IntegrationsPage"));
const OpenBankingAuthorizePage = lazy(() => import("./pages/customer/OpenBankingAuthorizePage"));
const OpenBankingConsentsPage = lazy(() => import("./pages/customer/OpenBankingConsentsPage"));
const OpenBankingAuthorizePaymentPage = lazy(() => import("./pages/customer/OpenBankingAuthorizePaymentPage"));
const BookkeepingPage = lazy(() => import("./pages/customer/BookkeepingPage"));
const BookkeepingInvoicesPage = lazy(() => import("./pages/customer/BookkeepingInvoicesPage"));
const BookkeepingContactsPage = lazy(() => import("./pages/customer/BookkeepingContactsPage"));
const BookkeepingVATPage = lazy(() => import("./pages/customer/BookkeepingVATPage"));
const BookkeepingReportsPage = lazy(() => import("./pages/customer/BookkeepingReportsPage"));
const BookkeepingInvoiceNewPage = lazy(() => import("./pages/customer/BookkeepingInvoiceNewPage"));
const BookkeepingInvoiceDetailPage = lazy(() => import("./pages/customer/BookkeepingInvoiceDetailPage"));
const ProgramPortalLayout = lazy(() => import("./pages/customer/program/ProgramPortalLayout"));
const ProgramDashboard = lazy(() => import("./pages/customer/program/ProgramDashboard"));
const ProgramPortalEndUsers = lazy(() => import("./pages/customer/program/ProgramPortalEndUsers"));
const ProgramPortalCards = lazy(() => import("./pages/customer/program/ProgramPortalCards"));
const ProgramPortalTeam = lazy(() => import("./pages/customer/program/ProgramPortalTeam"));
const ProgramPortalAPI = lazy(() => import("./pages/customer/program/ProgramPortalAPI"));
const ProgramPortalBranding = lazy(() => import("./pages/customer/program/ProgramPortalBranding"));
const CustomerProgramOrdersPage = lazy(() => import("./pages/customer/program/ProgramOrdersPage"));
const CustomerProgramTransactionsPage = lazy(() => import("./pages/customer/program/ProgramTransactionsPage"));
const CustomerProgramDisputesPage = lazy(() => import("./pages/customer/program/ProgramDisputesPage"));
const CustomerProgramControlsPage = lazy(() => import("./pages/customer/program/ProgramControlsPage"));
const CustomerProgramReportsPage = lazy(() => import("./pages/customer/program/ProgramReportsPage"));
const CustomerProgramSettingsPage = lazy(() => import("./pages/customer/program/ProgramSettingsPage"));

// ─── Admin pages ──────────────────────────────────────────────────────────────
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const ApplicationsList = lazy(() => import("./pages/admin/ApplicationsList"));
const ApplicationDetail = lazy(() => import("./pages/admin/ApplicationDetail"));
const RiskMatrix = lazy(() => import("./pages/admin/RiskMatrix"));
const ComplianceDashboard = lazy(() => import("./pages/admin/ComplianceDashboard"));
const ComplianceCommandCenter = lazy(() => import("./pages/admin/compliance/CommandCenter"));
const EmployeePerformancePage = lazy(() => import("./pages/admin/EmployeePerformancePage"));
const ComplianceSettings = lazy(() => import("./pages/admin/ComplianceSettings"));
const BankRegistryPage = lazy(() => import("./pages/admin/compliance/BankRegistryPage"));
const AdminTasksPage = lazy(() => import("./pages/admin/AdminTasksPage"));
const AdminInboxPage = lazy(() => import("./pages/admin/AdminInboxPage"));
const ActivityLog = lazy(() => import("./pages/admin/ActivityLog"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSessionsPage = lazy(() => import("./pages/admin/AdminSessionsPage"));
const ClientLifecyclePage = lazy(() => import("./pages/admin/ClientLifecyclePage"));
const ClientsList = lazy(() => import("./pages/admin/clients/ClientsList"));
const ClientDetailPage = lazy(() => import("./pages/admin/clients/ClientDetailPage"));
const ReferrersManagement = lazy(() => import("./pages/admin/ReferrersManagement"));
const ERMDashboard = lazy(() => import("./pages/admin/ERMDashboard"));
const KRIDashboard = lazy(() => import("./pages/admin/KRIDashboard"));
const IncidentDashboard = lazy(() => import("./pages/admin/IncidentDashboard"));
const VendorRiskDashboard = lazy(() => import("./pages/admin/VendorRiskDashboard"));
const BoardReportDashboard = lazy(() => import("./pages/admin/BoardReportDashboard"));
const OnboardingRemindersPage = lazy(() => import("./pages/admin/settings/OnboardingRemindersPage"));
const StressTestDashboard = lazy(() => import("./pages/admin/StressTestDashboard"));
const PolicyDashboard = lazy(() => import("./pages/admin/PolicyDashboard"));
const CaseDetail = lazy(() => import("./pages/admin/CaseDetail"));
const ComplianceCasesList = lazy(() => import("./pages/admin/compliance/ComplianceCasesList"));
const AccountControls = lazy(() => import("./pages/admin/compliance/AccountControls"));
const ClientControlsPage = lazy(() => import("./pages/admin/compliance/ClientControlsPage"));
const ComplianceReporting = lazy(() => import("./pages/admin/compliance/ComplianceReporting"));
const ResolutionPage = lazy(() => import("./pages/admin/compliance/Resolution"));
const MLROQueue = lazy(() => import("./pages/admin/compliance/MLROQueue"));
const HeldIntentsQueue = lazy(() => import("./pages/admin/compliance/HeldIntentsQueue"));
const HoldsDashboardPage = lazy(() => import("./pages/admin/compliance/HoldsDashboardPage"));
const SyncHealth = lazy(() => import("./pages/admin/SyncHealth"));
const DisputesList = lazy(() => import("./pages/admin/DisputesList"));
const FlowDiagrams = lazy(() => import("./pages/admin/FlowDiagrams"));
const DocsPage = lazy(() => import("./pages/admin/DocsPage"));
const PaaSGapAnalysisPage = lazy(() => import("./pages/admin/PaaSGapAnalysisPage"));
const PlansManagement = lazy(() => import("./pages/admin/PlansManagement"));
const BNBGarnishmentReportsPage = lazy(() => import("./pages/admin/compliance/BNBGarnishmentReportsPage"));

// Admin cards
const InventoryPage = lazy(() => import("./pages/admin/cards/InventoryPage"));
const FeeTablesPage = lazy(() => import("./pages/admin/cards/FeeTablesPage"));
const LimitTablesPage = lazy(() => import("./pages/admin/cards/LimitTablesPage"));
const PromotionsPage = lazy(() => import("./pages/admin/cards/PromotionsPage"));
const PlansPage = lazy(() => import("./pages/admin/cards/PlansPage"));
const CardProductsPage = lazy(() => import("./pages/admin/cards/CardProductsPage"));
const DesignLibraryPage = lazy(() => import("./pages/admin/cards/DesignLibraryPage"));
const TxConfirmationsPage = lazy(() => import("./pages/admin/cards/TxConfirmationsPage"));
const CardReportsPage = lazy(() => import("./pages/admin/cards/CardReportsPage"));
const PackagesCarriersPage = lazy(() => import("./pages/admin/cards/PackagesCarriersPage"));
const CardActivityHistoryPage = lazy(() => import("./pages/admin/cards/CardActivityHistoryPage"));
const CardSandboxPage = lazy(() => import("./pages/admin/cards/CardSandboxPage"));
const SystemAuditLogPage = lazy(() => import("./pages/admin/monitoring/SystemAuditLogPage"));
const SystemErrorLogPage = lazy(() => import("./pages/admin/monitoring/SystemErrorLogPage"));
const AnomalyDashboardPage = lazy(() => import("./pages/admin/monitoring/AnomalyDashboardPage"));
const CardTransactionsPage = lazy(() => import("./pages/admin/cards/CardTransactionsPage"));
const CardholdersPage = lazy(() => import("./pages/admin/cards/CardholdersPage"));
const AllCardsPage = lazy(() => import("./pages/admin/cards/AllCardsPage"));
const AdminCardDetailPage = lazy(() => import("./pages/admin/cards/AdminCardDetailPage"));
const AdminCardOrdersPage = lazy(() => import("./pages/admin/cards/AdminCardOrdersPage"));
const CardSimulator = lazy(() => import("./pages/admin/cards/CardSimulator"));

const CardLimitRules = lazy(() => import("./pages/admin/cards/CardLimitRules"));
const CardLimitRulesManager = lazy(() => import("./pages/admin/cards/CardLimitRulesManager"));
const CardMccLists = lazy(() => import("./pages/admin/cards/CardMccLists"));
const MerchantRiskRules = lazy(() => import("./pages/admin/cards/MerchantRiskRules"));
const CardFeeEvents = lazy(() => import("./pages/admin/cards/CardFeeEvents"));
const CardRuleRuns = lazy(() => import("./pages/admin/cards/CardRuleRuns"));

const DisputeDetailPage = lazy(() => import("./pages/admin/cards/DisputeDetailPage"));
const CardsSettlementRecon = lazy(() => import("./pages/admin/cards/CardsSettlementRecon"));
const CardsDisputeRecon = lazy(() => import("./pages/admin/cards/CardsDisputeRecon"));
const InquiriesPage = lazy(() => import("./pages/admin/cards/InquiriesPage"));
const LimitRequestsPage = lazy(() => import("./pages/admin/cards/LimitRequestsPage"));
const NewDisputesPage = lazy(() => import("./pages/admin/cards/disputes/NewDisputesPage"));
const InProgressDisputesPage = lazy(() => import("./pages/admin/cards/disputes/InProgressDisputesPage"));
const ClosedDisputesPage = lazy(() => import("./pages/admin/cards/disputes/ClosedDisputesPage"));
const AllDisputesPage = lazy(() => import("./pages/admin/cards/disputes/AllDisputesPage"));

// Ledger pages
const IbanRegistry = lazy(() => import("./pages/admin/ledger/IbanRegistry"));
const LedgerDashboard = lazy(() => import("./pages/admin/ledger/LedgerDashboard"));
const ChartOfAccounts = lazy(() => import("./pages/admin/ledger/ChartOfAccounts"));
const JournalEntries = lazy(() => import("./pages/admin/ledger/JournalEntries"));
const JournalTransactionRegister = lazy(() => import("./pages/admin/ledger/JournalTransactionRegister"));
// Transactions merged into JournalEntries
const Balances = lazy(() => import("./pages/admin/ledger/Balances"));
const Reconciliation = lazy(() => import("./pages/admin/ledger/Reconciliation"));
const LedgerReports = lazy(() => import("./pages/admin/ledger/LedgerReports"));
const RevenueAnalytics = lazy(() => import("./pages/admin/ledger/RevenueAnalytics"));
const SafeguardingReconciliation = lazy(() => import("./pages/admin/ledger/SafeguardingReconciliation"));
const AuditLog = lazy(() => import("./pages/admin/ledger/AuditLog"));
const Approvals = lazy(() => import("./pages/admin/ledger/Approvals"));
const PeriodClose = lazy(() => import("./pages/admin/ledger/PeriodClose"));
const LedgerFinancialReports = lazy(() => import("./pages/admin/ledger/LedgerFinancialReports"));
const ReconciliationImports = lazy(() => import("./pages/admin/ledger/ReconciliationImports"));
const LedgerAuditExports = lazy(() => import("./pages/admin/ledger/LedgerAuditExports"));
const RailStatusDashboard = lazy(() => import("./pages/admin/ledger/RailStatusDashboard"));
const ReceivingInstructionsAdmin = lazy(() => import("./pages/admin/ledger/ReceivingInstructionsAdmin"));
const StatementImportPage = lazy(() => import("./pages/admin/ledger/StatementImportPage"));
const LedgerIntegrityAudit = lazy(() => import("./pages/admin/ledger/LedgerIntegrityAudit"));
const TrialBalance = lazy(() => import("./pages/admin/ledger/TrialBalance"));
const BNBStatutoryPnL = lazy(() => import("./pages/admin/ledger/BNBStatutoryPnL"));
const InboundReturnQueue = lazy(() => import("./pages/admin/ledger/InboundReturnQueue"));
const ReturnsApprovalQueue = lazy(() => import("./pages/admin/ledger/ReturnsApprovalQueue"));

// Treasury pages
const FxPricingSpreadRules = lazy(() => import("./pages/admin/treasury/FxPricingSpreadRules"));
const FxPositions = lazy(() => import("./pages/admin/treasury/FxPositions"));
const ExecutionQueue = lazy(() => import("./pages/admin/treasury/ExecutionQueue"));
const TreasuryExecutionDesk = lazy(() => import("./pages/admin/treasury/TreasuryExecutionDesk"));
const AutopilotControl = lazy(() => import("./pages/admin/treasury/AutopilotControl"));
const ForwardMarginBands = lazy(() => import("./pages/admin/treasury/ForwardMarginBands"));
const ForwardOverrides = lazy(() => import("./pages/admin/treasury/ForwardOverrides"));
const ForwardBook = lazy(() => import("./pages/admin/treasury/ForwardBook"));

const TreasuryConfirmations = lazy(() => import("./pages/admin/treasury/TreasuryConfirmations"));
const TreasurySettlements = lazy(() => import("./pages/admin/treasury/TreasurySettlements"));
const EODChecklist = lazy(() => import("./pages/admin/treasury/EODChecklist"));
const MarketDataHealth = lazy(() => import("./pages/admin/treasury/MarketDataHealth"));
const FxClosingProviders = lazy(() => import("./pages/admin/treasury/FxClosingProviders"));
const CurrencyPairMatrix = lazy(() => import("./pages/admin/treasury/CurrencyPairMatrix"));
const TreasuryDashboard = lazy(() => import("./pages/admin/treasury/TreasuryDashboard"));

// PayNovus Operations
const TreasuryOperations = lazy(() => import("./pages/admin/operations/TreasuryOperations"));
const ExecutePayment = lazy(() => import("./pages/admin/operations/ExecutePayment"));
const OperatingStatement = lazy(() => import("./pages/admin/operations/OperatingStatement"));
const TeamManagement = lazy(() => import("./pages/admin/operations/TeamManagement"));

const AccountingIntegrationsAdmin = lazy(() => import("./pages/admin/AccountingIntegrationsAdmin"));

// Diagnostics pages
// SeedHarness removed — use ComprehensiveSeedPage instead
const RunE2E = lazy(() => import("./pages/admin/diagnostics/RunE2E"));
const ReleaseReadiness = lazy(() => import("./pages/admin/diagnostics/ReleaseReadiness"));
const UIAudit = lazy(() => import("./pages/admin/diagnostics/UIAudit"));
const WhoAmI = lazy(() => import("./pages/admin/diagnostics/WhoAmI"));
const DailyGoNoGo = lazy(() => import("./pages/admin/diagnostics/DailyGoNoGo"));

const GoLiveReadiness = lazy(() => import("./pages/admin/diagnostics/GoLiveReadiness"));
const RemediationDashboard = lazy(() => import("./pages/admin/diagnostics/RemediationDashboard"));
const CoreE2E = lazy(() => import("./pages/admin/diagnostics/CoreE2E"));
// CurrencycloudE2E removed — use CurrencycloudE2ESuite instead
const CurrencycloudE2ESuite = lazy(() => import("./pages/admin/diagnostics/CurrencycloudE2ESuite"));
const CCProvisioningDiagnostic = lazy(() => import("./pages/admin/diagnostics/CCProvisioningDiagnostic"));

const EmployeeTestDiagnostics = lazy(() => import("./pages/admin/diagnostics/EmployeeTestDiagnostics"));
const SecurityDiagnostics = lazy(() => import("./pages/admin/diagnostics/SecurityDiagnostics"));
const PSDReadiness = lazy(() => import("./pages/admin/diagnostics/PSDReadiness"));
const ActionCatalogReconciliation = lazy(() => import("./pages/admin/diagnostics/ActionCatalogReconciliation"));
const ComprehensiveSeedPage = lazy(() => import("./pages/admin/diagnostics/ComprehensiveSeedPage"));

const PMSmokeTest = lazy(() => import("./pages/admin/diagnostics/PMSmokeTest"));
const SupportTicketingE2E = lazy(() => import("./pages/admin/diagnostics/SupportTicketingE2E"));
// PMSeedHarness removed — use ComprehensiveSeedPage instead
const ResponsiveCheck = lazy(() => import("./pages/admin/diagnostics/ResponsiveCheck"));
const TransitionFSME2E = lazy(() => import("./pages/admin/diagnostics/TransitionFSME2E"));
const AcceptanceGate = lazy(() => import("./pages/admin/diagnostics/AcceptanceGate"));
const CardProgramE2E = lazy(() => import("./pages/admin/diagnostics/CardProgramE2E"));

const Phase32AuditEvidence = lazy(() => import("./pages/admin/diagnostics/Phase32AuditEvidence"));
const BanksE2E = lazy(() => import("./pages/admin/diagnostics/BanksE2E"));
const BaasE2E = lazy(() => import("./pages/admin/diagnostics/BaasE2E"));
const FinancialIntegrityE2E = lazy(() => import("./pages/admin/diagnostics/FinancialIntegrityE2E"));
const SepaCamtFixtures = lazy(() => import("./pages/admin/diagnostics/SepaCamtFixtures"));

const ReceivingInstructionsE2E = lazy(() => import("./pages/admin/diagnostics/ReceivingInstructionsE2E"));
const OnboardingIndividualE2E = lazy(() => import("./pages/admin/diagnostics/OnboardingIndividualE2E"));
const OnboardingCorporateE2E = lazy(() => import("./pages/admin/diagnostics/OnboardingCorporateE2E"));
const AdminClientMgmtE2E = lazy(() => import("./pages/admin/diagnostics/AdminClientMgmtE2E"));
const PaymentsFullE2E = lazy(() => import("./pages/admin/diagnostics/PaymentsFullE2E"));
const FxFullE2E = lazy(() => import("./pages/admin/diagnostics/FxFullE2E"));
const CardsFullE2Ev2 = lazy(() => import("./pages/admin/diagnostics/CardsFullE2Ev2"));
const ComplianceFullE2E = lazy(() => import("./pages/admin/diagnostics/ComplianceFullE2E"));
const OrchestrationE2E = lazy(() => import("./pages/admin/diagnostics/OrchestrationE2E"));


// System pages
const FeatureFlags = lazy(() => import("./pages/admin/system/FeatureFlags"));
const ProviderCutover = lazy(() => import("./pages/admin/system/ProviderCutover"));
const DemoPilot = lazy(() => import("./pages/admin/system/DemoPilot"));
const TemplateManagerPage = lazy(() => import("./pages/admin/system/TemplateManagerPage"));

// Integration pages
const CurrencycloudCapabilities = lazy(() => import("./pages/admin/integrations/CurrencycloudCapabilities"));
const CurrencycloudWebhooks = lazy(() => import("./pages/admin/integrations/CurrencycloudWebhooks"));
const CurrencycloudSandboxPack = lazy(() => import("./pages/admin/integrations/CurrencycloudSandboxPack"));
const CurrencyRegistry = lazy(() => import("./pages/admin/integrations/CurrencyRegistry"));
const BankAccountCheckerAdmin = lazy(() => import("./pages/admin/integrations/BankAccountCheckerAdmin"));
const BaaSPartnersPage = lazy(() => import("./pages/admin/integrations/BaaSPartnersPage"));
const HubSpotSettingsPage = lazy(() => import("./pages/admin/integrations/HubSpotSettingsPage"));
const GarnishmentRegisterPage = lazy(() => import("./pages/admin/compliance/GarnishmentRegisterPage"));
const BNBDeclarationsPage = lazy(() => import("./pages/admin/compliance/BNBDeclarationsPage"));
const WebhooksPage = lazy(() => import("./pages/admin/integrations/WebhooksPage"));
const IntegrationsRegistry = lazy(() => import("./pages/admin/integrations/IntegrationsRegistry"));
const WebhookMonitoringPage = lazy(() => import("./pages/admin/integrations/WebhookMonitoringPage"));

// BaaS admin pages
const AdminBaaSAllowlistPage = lazy(() => import("./pages/admin/baas/AdminBaaSAllowlistPage"));
const AdminBaaSRequestsPage = lazy(() => import("./pages/admin/baas/AdminBaaSRequestsPage"));
const BaaSManagementHub = lazy(() => import("./pages/admin/baas/BaaSManagementHub"));
const BaaSPartnerPricingPage = lazy(() => import("./pages/admin/baas/BaaSPartnerPricingPage"));
const BaaSPartnerFeesPage = lazy(() => import("./pages/admin/baas/BaaSPartnerFeesPage"));
const BaaSPartnerBillingPage = lazy(() => import("./pages/admin/baas/BaaSPartnerBillingPage"));
const BaaSPartnerLedgerPage = lazy(() => import("./pages/admin/baas/BaaSPartnerLedgerPage"));
const BaaSAdminSafeguardingPage = lazy(() => import("./pages/admin/baas/BaaSAdminSafeguardingPage"));
const BaaSAdminTicketsPage = lazy(() => import("./pages/admin/baas/BaaSAdminTicketsPage"));
const BaaSPartnerCompliancePage = lazy(() => import("./pages/admin/baas/BaaSPartnerCompliancePage"));
const BaaSAdminCollectionsPage = lazy(() => import("./pages/admin/baas/BaaSAdminCollectionsPage"));
const AdminSupportTicketsPage = lazy(() => import("./pages/admin/AdminSupportTicketsPage"));

// Payments pages
const RailHealthDashboard = lazy(() => import("./pages/admin/payments/RailHealthDashboard"));
const InboundOps = lazy(() => import("./pages/admin/payments/InboundOps"));
const BaaSInboundQueue = lazy(() => import("./pages/admin/payments/BaaSInboundQueue"));
const InboundSuspenseQueue = lazy(() => import("./pages/admin/payments/InboundSuspenseQueue"));
const StrandedIntentsPage = lazy(() => import("./pages/admin/payments/StrandedIntentsPage"));
const ReleaseRejectedQueue = lazy(() => import("./pages/admin/payments/ReleaseRejectedQueue"));
const InboundSuspenseDetail = lazy(() => import("./pages/admin/payments/InboundSuspenseDetail"));
const PaymentInvestigationsPage = lazy(() => import("./pages/admin/payments/PaymentInvestigationsPage"));
const PaymentInvestigationDetailPage = lazy(() => import("./pages/admin/payments/PaymentInvestigationDetailPage"));
const InvestigationPoliciesPage = lazy(() => import("./pages/admin/payments/InvestigationPoliciesPage"));
const SepaOutboundOps = lazy(() => import("./pages/admin/payments/SepaOutboundOps"));
const PaymentRecallsPage = lazy(() => import("./pages/admin/payments/PaymentRecallsPage"));
const SepaParticipantsPage = lazy(() => import("./pages/admin/payments/SepaParticipantsPage"));
const SepaDashboard = lazy(() => import("./pages/admin/payments/SepaDashboard"));
const SepaWebhooksPage = lazy(() => import("./pages/admin/payments/SepaWebhooksPage"));
const PaymentRailConfigPage = lazy(() => import("./pages/admin/payments/PaymentRailConfigPage"));
const PaymentOrchestration = lazy(() => import("./pages/admin/payments/PaymentOrchestration"));
const CCAccountsPage = lazy(() => import("./pages/admin/payments/CCAccountsPage"));

// Pricing pages
const PricingPlans = lazy(() => import("./pages/admin/pricing/PricingPlans"));
const UnifiedPricingHub = lazy(() => import("./pages/admin/pricing/UnifiedPricingHub"));

// Open Banking pages
const TPPRegistryPage = lazy(() => import("./pages/admin/openbanking/TPPRegistryPage"));
const OBConsentsPage = lazy(() => import("./pages/admin/openbanking/OBConsentsPage"));
const OBAccessLogPage = lazy(() => import("./pages/admin/openbanking/OBAccessLogPage"));
const OBSettingsPage = lazy(() => import("./pages/admin/openbanking/OBSettingsPage"));
const OBPaymentConsentsPage = lazy(() => import("./pages/admin/openbanking/OBPaymentConsentsPage"));
const OBPISPDashboard = lazy(() => import("./pages/admin/openbanking/OBPISPDashboard"));

// Bookkeeping admin pages
const AdminBookkeepingPage = lazy(() => import("./pages/admin/bookkeeping/AdminBookkeepingPage"));
const AdminInvoicesPage = lazy(() => import("./pages/admin/bookkeeping/AdminInvoicesPage"));
const AdminVATRatesPage = lazy(() => import("./pages/admin/bookkeeping/AdminVATRatesPage"));

// ISO 27001 pages
const ISO27001Page = lazy(() => import("./pages/admin/iso/ISO27001Page"));
const SoADashboard = lazy(() => import("./pages/admin/iso/SoADashboard"));
const EvidencePackDashboard = lazy(() => import("./pages/admin/iso/EvidencePackDashboard"));
const ControlTestingDashboard = lazy(() => import("./pages/admin/iso/ControlTestingDashboard"));

// Security pages
const SecurityUsersPage = lazy(() => import("./pages/admin/security/SecurityUsersPage"));
const ApprovalsPage = lazy(() => import("./pages/admin/security/ApprovalsPage"));
const SecurityAuditPage = lazy(() => import("./pages/admin/security/SecurityAuditPage"));
const PIILeakScan = lazy(() => import("./pages/admin/security/PIILeakScan"));
const LoginLockoutPage = lazy(() => import("./pages/admin/security/LoginLockoutPage"));
const SCAEventsPage = lazy(() => import("./pages/admin/security/SCAEventsPage"));
const IPAllowlistPage = lazy(() => import("./pages/admin/security/IPAllowlistPage"));
const DeviceAlertsPage = lazy(() => import("./pages/admin/security/DeviceAlertsPage"));

// Compliance pages
const WhitelistRegistryPage = lazy(() => import("./pages/admin/compliance/WhitelistRegistryPage"));
const CompliancePolicies = lazy(() => import("./pages/admin/compliance/CompliancePolicies"));
const ComplianceParametersV2 = lazy(() => import("./pages/admin/compliance/ComplianceParametersV2"));
const ScreeningQueue = lazy(() => import("./pages/admin/compliance/ScreeningQueue"));
const MonitoringAlerts = lazy(() => import("./pages/admin/compliance/MonitoringAlerts"));
const TransactionMonitoringPage = lazy(() => import("./pages/admin/compliance/TransactionMonitoringPage"));
const EDDRequestsPage = lazy(() => import("./pages/admin/compliance/EDDRequestsPage"));
const SARWorkspace = lazy(() => import("./pages/admin/compliance/SARWorkspace"));
const AuditReportsPage = lazy(() => import("./pages/admin/compliance/AuditReportsPage"));
const AutomationRulesPage = lazy(() => import("./pages/admin/compliance/AutomationRulesPage"));
const AutomationDiagnosticsPage = lazy(() => import("./pages/admin/compliance/AutomationDiagnosticsPage"));
const AutomationPage = lazy(() => import("./pages/admin/compliance/AutomationPage"));
const CounterpartyProfilesPage = lazy(() => import("./pages/admin/compliance/CounterpartyProfilesPage"));
const IPBlocklistPage = lazy(() => import("./pages/admin/compliance/IPBlocklistPage"));
const PaymentMethodWhitelistPage = lazy(() => import("./pages/admin/compliance/PaymentMethodWhitelistPage"));
const QAManagementPage = lazy(() => import("./pages/admin/compliance/QAManagementPage"));
const MonitoringIndexPage = lazy(() => import("./pages/admin/compliance/monitoring/MonitoringIndexPage"));
const TMRulesLibrary = lazy(() => import("./pages/admin/compliance/TMRulesLibrary"));
const TMParameters = lazy(() => import("./pages/admin/compliance/TMParameters"));
const TMRuns = lazy(() => import("./pages/admin/compliance/TMRuns"));
const TMCoveragePage = lazy(() => import("./pages/admin/compliance/TMCoveragePage"));
const TMCoverageDashboardPage = lazy(() => import("./pages/admin/compliance/TMCoverageDashboardPage"));
const InterdictionsPage = lazy(() => import("./pages/admin/compliance/tm/InterdictionsPage"));
const ControlsRegistryPage = lazy(() => import("./pages/admin/compliance/tm/ControlsRegistryPage"));
const AlertResolutionPage = lazy(() => import("./pages/admin/compliance/tm/AlertResolutionPage"));
const InterdictionPoliciesPage = lazy(() => import("./pages/admin/compliance/tm/InterdictionPoliciesPage"));
const MonitoringSettingsPage = lazy(() => import("./pages/admin/compliance/monitoring/MonitoringSettingsPage"));
const MonitoringTasksPage = lazy(() => import("./pages/admin/compliance/monitoring/MonitoringTasksPage"));
const MonitoringHistoryPage = lazy(() => import("./pages/admin/compliance/monitoring/MonitoringHistoryPage"));
const MIIndexPage = lazy(() => import("./pages/admin/compliance/mi/MIIndexPage"));
const MIOverviewPage = lazy(() => import("./pages/admin/compliance/mi/MIOverviewPage"));
const MITMEffectivenessPage = lazy(() => import("./pages/admin/compliance/mi/MITMEffectivenessPage"));
const MIInterdictionPage = lazy(() => import("./pages/admin/compliance/mi/MIInterdictionPage"));
const MIOngoingMonitoringPage = lazy(() => import("./pages/admin/compliance/mi/MIOngoingMonitoringPage"));
const MIScreeningSARPage = lazy(() => import("./pages/admin/compliance/mi/MIScreeningSARPage"));
const RuleTuningPage = lazy(() => import("./pages/admin/compliance/RuleTuningPage"));
const IndustryRiskPage = lazy(() => import("./pages/admin/compliance/IndustryRiskPage"));
const RiskMatrixVersionsPage = lazy(() => import("./pages/admin/compliance/RiskMatrixVersionsPage"));
const CompanyRiskAssessmentsPage = lazy(() => import("./pages/admin/compliance/CompanyRiskAssessmentsPage"));
const CompanyRiskAssessmentDetail = lazy(() => import("./pages/admin/compliance/CompanyRiskAssessmentDetail"));
const ComplianceListsPage = lazy(() => import("./pages/admin/compliance/ComplianceListsPage"));
const ComplianceExtractorsPage = lazy(() => import("./pages/admin/compliance/ComplianceExtractorsPage"));
const ExtractionResultsPage = lazy(() => import("./pages/admin/compliance/ExtractionResultsPage"));

// Ops pages
const OpsThresholds = lazy(() => import("./pages/admin/ops/OpsThresholds"));
const OpsRunbook = lazy(() => import("./pages/admin/ops/OpsRunbook"));
const OpsGovernanceLandingPage = lazy(() => import("./pages/admin/ops/OpsGovernanceLandingPage"));
const PartnerDueDiligencePack = lazy(() => import("./pages/admin/ops/PartnerDueDiligencePack"));
const OperationalIncidentsPage = lazy(() => import("./pages/admin/ops/OperationalIncidentsPage"));

// Card Program pages
const ProgramManagersPage = lazy(() => import("./pages/admin/card-program/ProgramManagersPage"));
const CardProgramsPage = lazy(() => import("./pages/admin/card-program/CardProgramsPage"));
const ProgramEndUsersPage = lazy(() => import("./pages/admin/card-program/ProgramEndUsersPage"));
const ProgramCardsPage = lazy(() => import("./pages/admin/card-program/ProgramCardsPage"));
const ProgramOrdersPage = lazy(() => import("./pages/admin/card-program/ProgramOrdersPage"));
const ProgramTransactionsPage = lazy(() => import("./pages/admin/card-program/ProgramTransactionsPage"));
const ProgramDisputesPage = lazy(() => import("./pages/admin/card-program/ProgramDisputesPage"));
const ProgramControlsPage = lazy(() => import("./pages/admin/card-program/ProgramControlsPage"));
const ProgramPricingPage = lazy(() => import("./pages/admin/card-program/ProgramPricingPage"));
const PricingPlansPage = lazy(() => import("./pages/admin/card-program/PricingPlansPage"));
const PricingPlanDetailPage = lazy(() => import("./pages/admin/card-program/PricingPlanDetailPage"));
const PricingVersionsPage = lazy(() => import("./pages/admin/card-program/PricingVersionsPage"));
const PricingFeeEventsPage = lazy(() => import("./pages/admin/card-program/PricingFeeEventsPage"));
const PricingRevenuePage = lazy(() => import("./pages/admin/card-program/PricingRevenuePage"));
const ProgramCompliancePage = lazy(() => import("./pages/admin/card-program/ProgramCompliancePage"));
const ComplianceOverviewPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceOverviewPage"));
const ComplianceProgramsPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceProgramsPage"));
const ComplianceScreeningPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceScreeningPage"));
const ComplianceAlertsPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceAlertsPage"));
const ComplianceCasesPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceCasesPage"));
const ComplianceReviewsPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceReviewsPage"));
const ComplianceDocumentsPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceDocumentsPage"));
const ComplianceRulesetsPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceRulesetsPage"));
const ComplianceEDDPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceEDDPage"));
const ComplianceSARPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceSARPage"));
const ComplianceMLROPage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceMLROPage"));
const ComplianceEvidencePage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceEvidencePage"));
const ProgramComplianceTimelinePage = lazy(() => import("./pages/admin/card-program/compliance/ComplianceTimelinePage"));
const ProgramSettlementPage = lazy(() => import("./pages/admin/card-program/ProgramSettlementPage"));
const ProgramFinancePage = lazy(() => import("./pages/admin/card-program/ProgramFinancePage"));
const ProgramReportsPage = lazy(() => import("./pages/admin/card-program/ProgramReportsPage"));
const ProgramAPIPage = lazy(() => import("./pages/admin/card-program/ProgramAPIPage"));
const ProgramBrandingPage = lazy(() => import("./pages/admin/card-program/ProgramBrandingPage"));
const ProgramAuditPage = lazy(() => import("./pages/admin/card-program/ProgramAuditPage"));

// Card Program Ops pages
const OpsOverviewPage = lazy(() => import("./pages/admin/card-program/ops/OpsOverviewPage"));
const SLADashboardPage = lazy(() => import("./pages/admin/card-program/ops/SLADashboardPage"));
const PortfolioRiskPage = lazy(() => import("./pages/admin/card-program/ops/PortfolioRiskPage"));
const PolicyRolloutsPage = lazy(() => import("./pages/admin/card-program/ops/PolicyRolloutsPage"));
const ChangeRequestsPage = lazy(() => import("./pages/admin/card-program/ops/ChangeRequestsPage"));
const KRIThresholdsPage = lazy(() => import("./pages/admin/card-program/ops/KRIThresholdsPage"));
const EmergencyActionsPage = lazy(() => import("./pages/admin/card-program/ops/EmergencyActionsPage"));
const OpsReportsPage = lazy(() => import("./pages/admin/card-program/ops/OpsReportsPage"));
const ControlsAssurancePage = lazy(() => import("./pages/admin/card-program/ops/ControlsAssurancePage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const AdminAuthLayout = () => (
  <AdminAuthProvider>
    <Outlet />
  </AdminAuthProvider>
);

const CustomerAuthLayout = () => (
  <CustomerAuthProvider>
    <CorporatePermissionProvider>
      <Outlet />
    </CorporatePermissionProvider>
  </CustomerAuthProvider>
);

const PMAuthLayout = () => (
  <PMAuthProvider>
    <Outlet />
  </PMAuthProvider>
);

const BaaSAuthLayout = () => (
  <BaaSAuthProvider>
    <Outlet />
  </BaaSAuthProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          {/* Single top-level Suspense with null fallback — no loading spinner, chunks load silently */}
          <Suspense fallback={null}>
            <Routes>
              {/* Customer login at root */}
              <Route element={<CustomerAuthLayout />}>
                <Route path="/" element={<CustomerLogin />} />
              </Route>

              {/* Onboarding at /app/onboard */}
              <Route path="/app/onboard" element={<Index />} />
              {/* Legacy redirect: old root onboarding */}
              <Route path="/onboard" element={<Navigate to="/app/onboard" replace />} />
              <Route path="/app/join" element={<JoinInvitation />} />
              <Route path="/pdf-preview" element={<PDFPreview />} />

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminAuthLayout />}>
                <Route path="login" element={<AdminLogin />} />
                <Route path="mfa-setup" element={<AdminMFASetup />} />
                <Route path="mfa-challenge" element={<AdminMFAChallenge />} />
                <Route path="reset-password" element={<Navigate to="/admin/set-password" replace />} />
                <Route path="set-password" element={<AdminSetPassword />} />
                <Route element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="applications" element={<Navigate to="/admin/applications/individual" replace />} />
                  <Route path="applications/individual" element={<ApplicationsList type="individual" />} />
                  <Route path="applications/corporate" element={<ApplicationsList type="corporate" />} />
                  <Route path="applications/:id" element={<ApplicationDetail />} />
                  <Route path="flows" element={<FlowDiagrams />} />
                  <Route path="flows/:category" element={<FlowDiagrams />} />
                  <Route path="risk-matrix" element={<RiskMatrix />} />
                  <Route path="compliance/command-center" element={<ComplianceCommandCenter />} />
                  <Route path="compliance" element={<Navigate to="/admin/compliance/command-center" replace />} />
                  <Route path="compliance/screening" element={<ScreeningQueue />} />
                  <Route path="compliance/alerts" element={<MonitoringAlerts />} />
                  <Route path="compliance/transaction-monitoring" element={<TransactionMonitoringPage />} />
                  <Route path="compliance/cases" element={<ComplianceCasesList />} />
                  <Route path="compliance/cases/:caseId" element={<CaseDetail />} />
                  <Route path="compliance/edd" element={<EDDRequestsPage />} />
                  <Route path="compliance/sar" element={<SARWorkspace />} />
                  <Route path="compliance/mlro" element={<MLROQueue />} />
                  <Route path="compliance/held-intents" element={<HeldIntentsQueue />} />
                  <Route path="compliance/holds-dashboard" element={<HoldsDashboardPage />} />
                  <Route path="compliance/account-controls" element={<AccountControls />} />
                  <Route path="compliance/client-controls" element={<ClientControlsPage />} />
                  <Route path="compliance/audit" element={<AuditReportsPage />} />
                  <Route path="compliance/qa" element={<QAManagementPage />} />
                  <Route path="compliance/tm/rules" element={<TMRulesLibrary />} />
                  <Route path="compliance/tm/parameters" element={<TMParameters />} />
                  <Route path="compliance/tm/runs" element={<TMRuns />} />
                  <Route path="compliance/tm/coverage" element={<TMCoveragePage />} />
                  <Route path="compliance/tm/coverage-dashboard" element={<TMCoverageDashboardPage />} />
                  <Route path="compliance/tm/interdictions" element={<InterdictionsPage />} />
                  <Route path="compliance/tm/controls" element={<ControlsRegistryPage />} />
                  <Route path="compliance/tm/resolution" element={<AlertResolutionPage />} />
                  <Route path="compliance/tm/policies" element={<InterdictionPoliciesPage />} />
                  <Route path="compliance/monitoring" element={<MonitoringIndexPage />}>
                    <Route index element={<Navigate to="tasks" replace />} />
                    <Route path="tasks" element={<MonitoringTasksPage />} />
                    <Route path="history" element={<MonitoringHistoryPage />} />
                    <Route path="settings" element={<MonitoringSettingsPage />} />
                  </Route>
                  <Route path="compliance/automation" element={<AutomationPage />}>
                    <Route index element={<Navigate to="rules" replace />} />
                    <Route path="rules" element={<AutomationRulesPage />} />
                    <Route path="diagnostics" element={<AutomationDiagnosticsPage />} />
                  </Route>
                  <Route path="compliance/automation-rules" element={<Navigate to="/admin/compliance/automation/rules" replace />} />
                  <Route path="compliance/counterparties" element={<CounterpartyProfilesPage />} />
                  <Route path="compliance/payment-whitelists" element={<PaymentMethodWhitelistPage />} />
                  <Route path="compliance/ip-blocklist" element={<IPBlocklistPage />} />
                  <Route path="compliance/automation-diagnostics" element={<Navigate to="/admin/compliance/automation/diagnostics" replace />} />
                  <Route path="compliance/mi" element={<MIIndexPage />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<MIOverviewPage />} />
                    <Route path="tm-effectiveness" element={<MITMEffectivenessPage />} />
                    <Route path="interdiction" element={<MIInterdictionPage />} />
                    <Route path="ongoing-monitoring" element={<MIOngoingMonitoringPage />} />
                    <Route path="screening-sar-edd" element={<MIScreeningSARPage />} />
                  </Route>
                  <Route path="compliance/banks" element={<BankRegistryPage />} />
                  <Route path="compliance/reporting" element={<ComplianceReporting />} />
                  <Route path="compliance/case-management" element={<Navigate to="/admin/compliance/cases" replace />} />
                  <Route path="compliance/resolution" element={<ResolutionPage />} />
                  <Route path="compliance/policies" element={<CompliancePolicies />} />
                  <Route path="compliance/parameters" element={<ComplianceParametersV2 />} />
                  <Route path="compliance/settings" element={<ComplianceSettings />} />
                  <Route path="compliance/industry-risk" element={<IndustryRiskPage />} />
                  <Route path="compliance/risk-matrix-versions" element={<RiskMatrixVersionsPage />} />
                  <Route path="compliance/risk-assessments" element={<CompanyRiskAssessmentsPage />} />
                  <Route path="compliance/risk-assessments/:assessmentId" element={<CompanyRiskAssessmentDetail />} />
                  <Route path="compliance/lists" element={<ComplianceListsPage />} />
                  <Route path="compliance/extractors" element={<ComplianceExtractorsPage />} />
                  <Route path="compliance/extraction-results" element={<ExtractionResultsPage />} />
                  <Route path="erm" element={<ERMDashboard />} />
                  <Route path="kri" element={<KRIDashboard />} />
                  <Route path="incidents" element={<IncidentDashboard />} />
                  <Route path="vendors" element={<VendorRiskDashboard />} />
                  <Route path="board-reports" element={<BoardReportDashboard />} />
                  <Route path="settings/onboarding-reminders" element={<OnboardingRemindersPage />} />
                  <Route path="stress-tests" element={<StressTestDashboard />} />
                  <Route path="policies" element={<PolicyDashboard />} />
                  <Route path="sync-health" element={<SyncHealth />} />
                  <Route path="activity" element={<ActivityLog />} />
                  <Route path="docs" element={<DocsPage />} />
                  <Route path="paas-gap-analysis" element={<PaaSGapAnalysisPage />} />
                  <Route path="settings" element={<Navigate to="/admin/security/users?tab=profile" replace />} />
                  <Route path="sessions" element={<AdminSessionsPage />} />
                  <Route path="tasks" element={<AdminTasksPage />} />
                  <Route path="inbox" element={<AdminInboxPage />} />
                  <Route path="performance" element={<EmployeePerformancePage />} />
                  <Route path="clients" element={<ClientsList />} />
                  <Route path="clients/:clientId" element={<ClientDetailPage />} />
                  <Route path="referrers" element={<ReferrersManagement />} />
                  <Route path="client-lifecycle" element={<ClientLifecyclePage />} />

                  {/* Ledger Routes */}
                  <Route path="ledger" element={<LedgerDashboard />} />
                  <Route path="ledger/accounts" element={<ChartOfAccounts />} />
                  <Route path="ledger/trial-balance" element={<TrialBalance />} />
                  <Route path="ledger/bnb-pnl" element={<BNBStatutoryPnL />} />
                  <Route path="ledger/journal" element={<JournalEntries />} />
                  <Route path="ledger/journal-register" element={<JournalTransactionRegister />} />
                  <Route path="ledger/transactions" element={<JournalEntries />} />
                  <Route path="ledger/balances" element={<Balances />} />
                  <Route path="ledger/revenue" element={<RevenueAnalytics />} />
                  <Route path="ledger/reconciliation" element={<Reconciliation />} />
                  <Route path="ledger/integrity-audit" element={<LedgerIntegrityAudit />} />
                  <Route path="ledger/safeguarding" element={<SafeguardingReconciliation />} />
                  <Route path="ledger/reports" element={<LedgerAuditExports />} />
                  <Route path="ledger/period-close" element={<PeriodClose />} />
                  <Route path="ledger/financial-reports" element={<LedgerFinancialReports />} />
                  <Route path="ledger/iban-registry" element={<IbanRegistry />} />
                  <Route path="ledger/receiving-instructions" element={<ReceivingInstructionsAdmin />} />
                  <Route path="ledger/audit" element={<AuditLog />} />
                  <Route path="ledger/approvals" element={<Approvals />} />
                  <Route path="ledger/inbound-return-queue" element={<InboundReturnQueue />} />
                  <Route path="ledger/returns-approval-queue" element={<ReturnsApprovalQueue />} />
                  <Route path="ledger/imports" element={<ReconciliationImports />} />
                  <Route path="ledger/rail-status" element={<RailStatusDashboard />} />
                  <Route path="ledger/statements" element={<StatementImportPage />} />

                  {/* Treasury Routes */}
                  <Route path="treasury" element={<TreasuryDashboard />} />
                  <Route path="treasury/pricing" element={<Navigate to="/admin/pricing?section=fx" replace />} />
                  <Route path="treasury/positions" element={<FxPositions />} />
                  <Route path="treasury/position-desk" element={<TreasuryExecutionDesk />} />
                  <Route path="treasury/execution-queue" element={<TreasuryExecutionDesk />} />
                  <Route path="treasury/execution-desk" element={<TreasuryExecutionDesk />} />
                  <Route path="treasury/autopilot" element={<AutopilotControl />} />
                  <Route path="treasury/forward-margins" element={<ForwardMarginBands />} />
                  <Route path="treasury/forward-overrides" element={<ForwardOverrides />} />
                  <Route path="treasury/forward-book" element={<ForwardBook />} />
                  <Route path="treasury/hedge-tickets" element={<TreasuryExecutionDesk />} />
                  <Route path="treasury/confirmations" element={<TreasuryConfirmations />} />
                  <Route path="treasury/settlements" element={<TreasurySettlements />} />
                  <Route path="treasury/eod" element={<EODChecklist />} />
                  <Route path="treasury/market-data" element={<MarketDataHealth />} />
                  <Route path="treasury/closing-providers" element={<FxClosingProviders />} />
                  <Route path="treasury/pair-matrix" element={<CurrencyPairMatrix />} />
                  <Route path="treasury/spread-rules" element={<Navigate to="/admin/pricing?section=fx" replace />} />

                  {/* Pricing & Billing */}
                  <Route path="pricing" element={<UnifiedPricingHub />} />
                  <Route path="plans" element={<Navigate to="/admin/pricing" replace />} />
                  <Route path="pricing/plans" element={<Navigate to="/admin/pricing?section=banking" replace />} />
                  <Route path="pricing/rules" element={<Navigate to="/admin/pricing?section=banking&ptab=plans" replace />} />
                  <Route path="pricing/events" element={<Navigate to="/admin/pricing?section=banking&ptab=events" replace />} />
                  <Route path="pricing/payment-fees" element={<Navigate to="/admin/pricing?section=banking&ptab=schedule" replace />} />
                  <Route path="pricing/limits" element={<Navigate to="/admin/pricing?section=limits" replace />} />

                  {/* Bookkeeping */}
                  <Route path="bookkeeping" element={<AdminBookkeepingPage />} />
                  <Route path="bookkeeping/invoices" element={<AdminInvoicesPage />} />
                  <Route path="bookkeeping/vat-rates" element={<AdminVATRatesPage />} />
                  <Route path="integrations/accounting" element={<AccountingIntegrationsAdmin />} />

                  {/* Open Banking */}
                  <Route path="openbanking/tpps" element={<TPPRegistryPage />} />
                  <Route path="openbanking/consents" element={<OBConsentsPage />} />
                  <Route path="openbanking/access-log" element={<OBAccessLogPage />} />
                  <Route path="openbanking/settings" element={<OBSettingsPage />} />
                  <Route path="openbanking/payment-consents" element={<OBPaymentConsentsPage />} />
                  <Route path="openbanking/pisp-dashboard" element={<OBPISPDashboard />} />

                  {/* ISO 27001 */}
                  <Route path="iso27001" element={<ISO27001Page />} />
                  <Route path="iso/soa" element={<SoADashboard />} />
                  <Route path="iso/evidence" element={<EvidencePackDashboard />} />
                  <Route path="iso/testing" element={<ControlTestingDashboard />} />

                  {/* Security & Approvals */}
                  <Route path="security/users" element={<SecurityUsersPage />} />
                  <Route path="approvals" element={<ApprovalsPage />} />
                  <Route path="security/audit" element={<SecurityAuditPage />} />
                  <Route path="security/sca-events" element={<SCAEventsPage />} />
                  <Route path="security/pii-leak-scan" element={<PIILeakScan />} />
                  <Route path="security/lockout" element={<LoginLockoutPage />} />
                  <Route path="security/ip-allowlist" element={<IPAllowlistPage />} />
                  <Route path="security/device-alerts" element={<DeviceAlertsPage />} />
                  <Route path="security/audit-log" element={<Navigate to="/admin/security/audit" replace />} />
                  <Route path="compliance/audit-log" element={<Navigate to="/admin/compliance/audit" replace />} />

                  {/* Cards */}
                  <Route path="cards" element={<Navigate to="/admin/cards/all-cards" replace />} />
                  <Route path="cards/all-cards" element={<AllCardsPage />} />
                  <Route path="cards/all-cards/:cardId" element={<AdminCardDetailPage />} />
                  <Route path="cards/cardholders" element={<CardholdersPage />} />
                  <Route path="cards/transactions" element={<CardTransactionsPage />} />
                  <Route path="cards/orders" element={<AdminCardOrdersPage />} />
                  <Route path="cards/disputes/new" element={<NewDisputesPage />} />
                  <Route path="cards/disputes/in-progress" element={<InProgressDisputesPage />} />
                  <Route path="cards/disputes/closed" element={<ClosedDisputesPage />} />
                  <Route path="cards/disputes/all" element={<AllDisputesPage />} />
                  <Route path="cards/disputes/reconciliation" element={<CardsDisputeRecon />} />
                  <Route path="cards/disputes/:id" element={<DisputeDetailPage />} />
                  <Route path="cards/disputes" element={<Navigate to="/admin/cards/disputes/all" replace />} />
                  <Route path="cards/simulator" element={<Navigate to="/admin/cards/sandbox" replace />} />
                  <Route path="cards/reconciliation" element={<CardsSettlementRecon />} />
                  <Route path="cards/inquiries" element={<InquiriesPage />} />
                  <Route path="cards/limit-requests" element={<LimitRequestsPage />} />
                  
                  <Route path="cards/fee-rules" element={<Navigate to="/admin/cards/fee-tables?tab=rules" replace />} />
                  <Route path="cards/fees" element={<Navigate to="/admin/cards/fee-tables?tab=rules" replace />} />
                  <Route path="cards/fee-events" element={<Navigate to="/admin/cards/fee-tables?tab=events" replace />} />
                  <Route path="cards/limit-rules" element={<CardLimitRulesManager />} />
                  <Route path="cards/mcc-lists" element={<CardMccLists />} />
                  <Route path="cards/merchant-risk" element={<MerchantRiskRules />} />
                  <Route path="cards/rule-runs" element={<CardRuleRuns />} />
                  {/* cards/bin-metadata and cards/3ds removed — underlying tables don't exist */}
                  <Route path="cards/products" element={<CardProductsPage />} />
                  <Route path="cards/designs" element={<DesignLibraryPage />} />
                  <Route path="cards/inventory" element={<InventoryPage />} />
                  <Route path="cards/fee-tables" element={<FeeTablesPage />} />
                  <Route path="cards/limits" element={<LimitTablesPage />} />
                  <Route path="cards/promotions" element={<PromotionsPage />} />
                  <Route path="cards/plans" element={<PlansPage />} />
                  <Route path="cards/packages" element={<PackagesCarriersPage />} />
                  <Route path="cards/tx-confirmations" element={<TxConfirmationsPage />} />
                  <Route path="cards/reports" element={<CardReportsPage />} />
                  <Route path="cards/activity-history" element={<CardActivityHistoryPage />} />
                  <Route path="cards/sandbox" element={<CardSandboxPage />} />

                  {/* System Monitoring */}
                  <Route path="monitoring/audit-log" element={<SystemAuditLogPage />} />
                  <Route path="monitoring/error-log" element={<SystemErrorLogPage />} />
                  <Route path="monitoring/anomaly-detection" element={<AnomalyDashboardPage />} />

                  {/* Diagnostics */}
                  <Route path="diagnostics/seed" element={<Navigate to="/admin/diagnostics/comprehensive-seed" replace />} />
                  <Route path="diagnostics/run-e2e" element={<RunE2E />} />
                  <Route path="diagnostics/release-readiness" element={<ReleaseReadiness />} />
                  <Route path="diagnostics/ui-audit" element={<UIAudit />} />
                  <Route path="diagnostics/whoami" element={<WhoAmI />} />
                  <Route path="diagnostics/daily-go-no-go" element={<DailyGoNoGo />} />
                  
                  <Route path="diagnostics/go-live-readiness" element={<GoLiveReadiness />} />
                  <Route path="diagnostics/remediation" element={<RemediationDashboard />} />
                  <Route path="diagnostics/core-e2e" element={<CoreE2E />} />
                  <Route path="diagnostics/currencycloud-e2e" element={<CurrencycloudE2ESuite />} />
                  <Route path="diagnostics/cc-provisioning" element={<CCProvisioningDiagnostic />} />
                  <Route path="diagnostics/currencycloud-e2e-suite" element={<Navigate to="/admin/diagnostics/currencycloud-e2e" replace />} />
                  <Route path="diagnostics/currencycloud-suite-e2e" element={<Navigate to="/admin/diagnostics/currencycloud-e2e" replace />} />
                  <Route path="diagnostics/banks-e2e" element={<BanksE2E />} />
                  <Route path="diagnostics/baas-e2e" element={<BaasE2E />} />
                  <Route path="diagnostics/financial-integrity-e2e" element={<FinancialIntegrityE2E />} />
                  
                  <Route path="diagnostics/employee-test" element={<EmployeeTestDiagnostics />} />
                  <Route path="diagnostics/security" element={<SecurityDiagnostics />} />
                  <Route path="diagnostics/psd-readiness" element={<PSDReadiness />} />
                  <Route path="diagnostics/action-catalog" element={<ActionCatalogReconciliation />} />
                  
                  <Route path="diagnostics/receiving-instructions-e2e" element={<ReceivingInstructionsE2E />} />
                  <Route path="diagnostics/pm-seed" element={<Navigate to="/admin/diagnostics/comprehensive-seed" replace />} />
                  <Route path="diagnostics/responsive-check" element={<ResponsiveCheck />} />
                  <Route path="diagnostics/comprehensive-seed" element={<ComprehensiveSeedPage />} />
                  <Route path="diagnostics/card-program-e2e" element={<CardProgramE2E />} />
                  
                  <Route path="diagnostics/transition-fsm-e2e" element={<TransitionFSME2E />} />
                  <Route path="diagnostics/acceptance-gate" element={<AcceptanceGate />} />
                  <Route path="diagnostics/card-program-phase32" element={<Phase32AuditEvidence />} />
                  <Route path="diagnostics/sepa-camt-fixtures" element={<SepaCamtFixtures />} />
                  
                 <Route path="diagnostics/pm-smoke-test" element={<PMSmokeTest />} />
                 <Route path="diagnostics/support-ticketing-e2e" element={<SupportTicketingE2E />} />
                  <Route path="diagnostics/onboarding-individual-e2e" element={<OnboardingIndividualE2E />} />
                  <Route path="diagnostics/onboarding-corporate-e2e" element={<OnboardingCorporateE2E />} />
                  <Route path="diagnostics/admin-client-mgmt-e2e" element={<AdminClientMgmtE2E />} />
                  <Route path="diagnostics/payments-full-e2e" element={<PaymentsFullE2E />} />
                  <Route path="diagnostics/fx-full-e2e" element={<FxFullE2E />} />
                  <Route path="diagnostics/cards-full-e2e-v2" element={<CardsFullE2Ev2 />} />
                  <Route path="diagnostics/compliance-full-e2e" element={<ComplianceFullE2E />} />
                  <Route path="diagnostics/orchestration-e2e" element={<OrchestrationE2E />} />

                  {/* PayNovus Operations */}
                  <Route path="operations/treasury" element={<TreasuryOperations />} />
                  <Route path="operations/payments" element={<ExecutePayment />} />
                  <Route path="operations/statement" element={<OperatingStatement />} />
                  <Route path="operations/team" element={<TeamManagement />} />

                  {/* System */}
                  <Route path="system/feature-flags" element={<FeatureFlags />} />
                  <Route path="system/cutover" element={<ProviderCutover />} />
                  <Route path="system/demo-pilot" element={<DemoPilot />} />
                  <Route path="system/templates" element={<TemplateManagerPage />} />

                  {/* Integrations */}
                  <Route path="integrations/currencycloud/capabilities" element={<CurrencycloudCapabilities />} />
                  <Route path="integrations/currencycloud/webhooks" element={<CurrencycloudWebhooks />} />
                  <Route path="integrations/currencycloud/sandbox-pack" element={<CurrencycloudSandboxPack />} />
                  <Route path="integrations/currencies" element={<CurrencyRegistry />} />
                  <Route path="integrations/bankaccountchecker" element={<BankAccountCheckerAdmin />} />
                  <Route path="baas" element={<BaaSManagementHub />} />
                  <Route path="integrations/baas/partners" element={<BaaSPartnersPage />} />
                  <Route path="baas/partners/:id/pricing" element={<BaaSPartnerPricingPage />} />
                  <Route path="baas/partners/:id/fees" element={<BaaSPartnerFeesPage />} />
                  <Route path="baas/partners/:id/billing" element={<BaaSPartnerBillingPage />} />
                  <Route path="baas/partners/:id/ledger" element={<BaaSPartnerLedgerPage />} />
                  <Route path="baas/partners/:id/safeguarding" element={<BaaSAdminSafeguardingPage />} />
                  <Route path="baas/partners/:id/compliance" element={<BaaSPartnerCompliancePage />} />
                  <Route path="baas/tickets" element={<BaaSAdminTicketsPage />} />
                  <Route path="support-tickets" element={<AdminSupportTicketsPage />} />
                  <Route path="baas/partners/:id/collections" element={<BaaSAdminCollectionsPage />} />
                  <Route path="baas/partners/:id/allowlist" element={<AdminBaaSAllowlistPage />} />
                  <Route path="baas/requests" element={<AdminBaaSRequestsPage />} />
                  <Route path="integrations" element={<IntegrationsRegistry />} />
                  <Route path="integrations/registry" element={<IntegrationsRegistry />} />
                  <Route path="integrations/webhooks" element={<WebhooksPage />} />
                  <Route path="integrations/webhook-monitor" element={<WebhookMonitoringPage />} />
                  <Route path="integrations/hubspot" element={<HubSpotSettingsPage />} />

                  {/* Payments */}
                  <Route path="payments/rail-health" element={<RailHealthDashboard />} />
                  <Route path="payments/sepa-dashboard" element={<SepaDashboard />} />
                  <Route path="payments/sepa-webhooks" element={<SepaWebhooksPage />} />
                  <Route path="payments/inbound" element={<BaaSInboundQueue />} />
                  <Route path="payments/inbound/legacy" element={<InboundOps />} />
                  <Route path="payments/inbound-suspense" element={<InboundSuspenseQueue />} />
                  <Route path="payments/inbound-suspense/:id" element={<InboundSuspenseDetail />} />
                  <Route path="payments/stranded" element={<StrandedIntentsPage />} />
                  <Route path="payment-ops/stranded" element={<StrandedIntentsPage />} />
                  <Route path="payments/release-rejected" element={<ReleaseRejectedQueue />} />
                  <Route path="payments/investigations" element={<PaymentInvestigationsPage />} />
                  <Route path="payments/investigations/:id" element={<PaymentInvestigationDetailPage />} />
                  <Route path="payments/investigations/policies" element={<InvestigationPoliciesPage />} />
                  <Route path="payments/outbound" element={<SepaOutboundOps />} />
                  {/* Direct payment id deep-link: redirects to outbound ops with the id pre-selected */}
                  <Route path="payments/:paymentId" element={<SepaOutboundOps />} />
                  <Route path="payments/recalls" element={<PaymentRecallsPage />} />
                  <Route path="payments/participants" element={<SepaParticipantsPage />} />
                  <Route path="payments/rail-config" element={<PaymentRailConfigPage />} />
                  <Route path="payments/orchestration" element={<PaymentOrchestration />} />
                  <Route path="payments/cc-accounts" element={<CCAccountsPage />} />

                  {/* Compliance extras */}
                  <Route path="compliance/whitelists" element={<WhitelistRegistryPage />} />
                  <Route path="compliance/garnishments" element={<GarnishmentRegisterPage />} />
                  <Route path="compliance/bnb-reports" element={<BNBGarnishmentReportsPage />} />
                  <Route path="compliance/bnb-declarations" element={<BNBDeclarationsPage />} />
                  <Route path="compliance/amla-declarations" element={<Navigate to="/admin/compliance/bnb-declarations" replace />} />
                  <Route path="compliance/monitoring-parameters" element={<DeprecatedRoute newPath="/admin/compliance/tm/parameters" label="Monitoring Parameters" />} />
                  <Route path="compliance/rule-tuning" element={<RuleTuningPage />} />

                  {/* Ops */}
                  <Route path="ops" element={<OpsGovernanceLandingPage />} />
                  <Route path="ops/thresholds" element={<OpsThresholds />} />
                  <Route path="ops/runbook" element={<OpsRunbook />} />
                  <Route path="ops/due-diligence" element={<PartnerDueDiligencePack />} />
                  <Route path="ops/incidents" element={<OperationalIncidentsPage />} />

                  {/* Card Program (B2B2C) */}
                  <Route path="card-program/managers" element={<ProgramManagersPage />} />
                  <Route path="card-program/programs" element={<CardProgramsPage />} />
                  <Route path="card-program/end-users" element={<ProgramEndUsersPage />} />
                  <Route path="card-program/cards" element={<ProgramCardsPage />} />
                  <Route path="card-program/orders" element={<ProgramOrdersPage />} />
                  <Route path="card-program/transactions" element={<ProgramTransactionsPage />} />
                  <Route path="card-program/disputes" element={<ProgramDisputesPage />} />
                  <Route path="card-program/controls" element={<ProgramControlsPage />} />
                  <Route path="card-program/pricing" element={<ProgramPricingPage />} />
                  <Route path="card-program/pricing/plans" element={<PricingPlansPage />} />
                  <Route path="card-program/pricing/plans/:id" element={<PricingPlanDetailPage />} />
                  <Route path="card-program/pricing/versions" element={<PricingVersionsPage />} />
                  <Route path="card-program/pricing/fee-events" element={<PricingFeeEventsPage />} />
                  <Route path="card-program/pricing/revenue" element={<PricingRevenuePage />} />
                  <Route path="card-program/compliance" element={<ProgramCompliancePage />} />
                  <Route path="card-program/compliance/overview" element={<ComplianceOverviewPage />} />
                  <Route path="card-program/compliance/programs" element={<ComplianceProgramsPage />} />
                  <Route path="card-program/compliance/screening" element={<ComplianceScreeningPage />} />
                  <Route path="card-program/compliance/alerts" element={<ComplianceAlertsPage />} />
                  <Route path="card-program/compliance/cases" element={<ComplianceCasesPage />} />
                  <Route path="card-program/compliance/reviews" element={<ComplianceReviewsPage />} />
                  <Route path="card-program/compliance/documents" element={<ComplianceDocumentsPage />} />
                  <Route path="card-program/compliance/rulesets" element={<ComplianceRulesetsPage />} />
                  <Route path="card-program/compliance/edd" element={<ComplianceEDDPage />} />
                  <Route path="card-program/compliance/sar" element={<ComplianceSARPage />} />
                  <Route path="card-program/compliance/mlro" element={<ComplianceMLROPage />} />
                  <Route path="card-program/compliance/evidence" element={<ComplianceEvidencePage />} />
                  <Route path="card-program/compliance/timeline" element={<ProgramComplianceTimelinePage />} />
                  <Route path="card-program/settlement" element={<ProgramSettlementPage />} />
                  <Route path="card-program/finance" element={<ProgramFinancePage />} />
                  <Route path="card-program/ops/overview" element={<OpsOverviewPage />} />
                  <Route path="card-program/ops/sla" element={<SLADashboardPage />} />
                  <Route path="card-program/ops/portfolio-risk" element={<PortfolioRiskPage />} />
                  <Route path="card-program/ops/policy-rollouts" element={<PolicyRolloutsPage />} />
                  <Route path="card-program/ops/change-requests" element={<ChangeRequestsPage />} />
                  <Route path="card-program/ops/kri-thresholds" element={<KRIThresholdsPage />} />
                  <Route path="card-program/ops/emergency-actions" element={<EmergencyActionsPage />} />
                  <Route path="card-program/ops/reports" element={<OpsReportsPage />} />
                  <Route path="card-program/ops/controls-assurance" element={<ControlsAssurancePage />} />
                  <Route path="card-program/reports" element={<ProgramReportsPage />} />
                  <Route path="card-program/api" element={<ProgramAPIPage />} />
                  <Route path="card-program/branding" element={<ProgramBrandingPage />} />
                  <Route path="card-program/audit" element={<ProgramAuditPage />} />
                </Route>
              </Route>

              {/* Customer Banking Routes */}
              <Route path="/app" element={<CustomerAuthLayout />}>
                <Route path="login" element={<Navigate to="/" replace />} />
                <Route path="mfa-setup" element={<CustomerMFASetup />} />
                <Route path="mfa-verify" element={<CustomerMFAChallenge />} />
                <Route path="reset-password" element={<CustomerResetPassword />} />
                <Route path="set-password" element={<CustomerResetPassword />} />
                <Route element={<CustomerLayout />}>
                  <Route index element={<CustomerHome />} />
                  <Route path="accounts" element={<AccountsList />} />
                  <Route path="accounts/new" element={<OpenAccountPage />} />
                  <Route path="accounts/:id" element={<AccountDetail />} />
                  <Route path="held-amounts" element={<HeldAmountsPage />} />
                  <Route path="beneficiaries" element={<BeneficiariesPage />} />
                  <Route path="payments" element={<PaymentsPage />} />
                  <Route path="transfer" element={<InternalTransferPage />} />
                  <Route path="request" element={<PaymentRequestsPage />} />
                  <Route path="exchange" element={<ExchangePage />} />
                  <Route path="cards" element={<CardsPage />} />
                  <Route path="cards/:id" element={<CardDetailPage />} />
                  <Route path="cards/order" element={<CardOrderPage />} />
                  <Route path="cards/benefits" element={<CardBenefitsPage />} />
                  <Route path="3ds/challenge/:challengeId" element={<ThreeDSChallengePage />} />
                  <Route path="plan" element={<PlanPage />} />
                  <Route path="plan/benefits" element={<PlanBenefitsPage />} />
                  <Route path="cashback" element={<CashbackPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="settings/personal" element={<PersonalDetailsPage />} />
                  <Route path="settings/fees" element={<FeesPage />} />
                  <Route path="settings/connected-apps" element={<OpenBankingConsentsPage />} />
                  <Route path="settings/connected-apps/authorize" element={<OpenBankingAuthorizePage />} />
                  <Route path="settings/connected-apps/authorize-payment" element={<OpenBankingAuthorizePaymentPage />} />
                  <Route path="bookkeeping" element={<BookkeepingPage />} />
                  <Route path="bookkeeping/invoices" element={<BookkeepingInvoicesPage />} />
                  <Route path="bookkeeping/invoices/new" element={<BookkeepingInvoiceNewPage />} />
                  <Route path="bookkeeping/invoices/:id" element={<BookkeepingInvoiceDetailPage />} />
                  <Route path="bookkeeping/contacts" element={<BookkeepingContactsPage />} />
                  <Route path="bookkeeping/vat" element={<BookkeepingVATPage />} />
                  <Route path="bookkeeping/reports" element={<BookkeepingReportsPage />} />
                  <Route path="settings/appearance" element={<AppearancePage />} />
                  <Route path="settings/avatar" element={<AvatarPage />} />
                  <Route path="settings/reminders" element={<RemindersPage />} />
                  <Route path="settings/verification" element={<VerificationStatusPage />} />
                  <Route path="settings/documents" element={<DocumentsPage />} />
                  <Route path="settings/notifications" element={<NotificationsSettingsPage />} />
                  <Route path="settings/accounting" element={<AccountingIntegrationsPage />} />
                  <Route path="settings/accounting/callback" element={<AccountingCallbackPage />} />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route path="integrations/*" element={<IntegrationsPage />} />
                  <Route path="statements" element={<StatementsPage />} />
                  <Route path="sessions" element={<SessionsPage />} />
                  <Route path="support" element={<SupportPage />} />
                  <Route path="support/:ticketId" element={<SupportTicketPage />} />
                  <Route path="requests" element={<Navigate to="/app/support" replace />} />
                  <Route path="team" element={<CorporateTeamPage />} />
                  <Route path="settings/users" element={<CorporateUsersPage />} />
                  <Route path="chat" element={<ChatPage />} />

                  {/* Program Manager Portal */}
                  <Route path="program" element={<ProgramPortalLayout />}>
                    <Route index element={<ProgramDashboard />} />
                    <Route path="end-users" element={<ProgramPortalEndUsers />} />
                    <Route path="cards" element={<ProgramPortalCards />} />
                    <Route path="orders" element={<CustomerProgramOrdersPage />} />
                    <Route path="transactions" element={<CustomerProgramTransactionsPage />} />
                    <Route path="disputes" element={<CustomerProgramDisputesPage />} />
                    <Route path="controls" element={<CustomerProgramControlsPage />} />
                    <Route path="reports" element={<CustomerProgramReportsPage />} />
                    <Route path="team" element={<ProgramPortalTeam />} />
                    <Route path="api" element={<ProgramPortalAPI />} />
                    <Route path="branding" element={<ProgramPortalBranding />} />
                    <Route path="settings" element={<CustomerProgramSettingsPage />} />
                  </Route>
                </Route>
              </Route>

              {/* Program Manager Portal — Separate app shell at /pm/* */}
              <Route path="/pm" element={<PMAuthLayout />}>
                <Route path="login" element={<PMLogin />} />
                <Route path="mfa-setup" element={<PMMFASetup />} />
                <Route path="mfa-verify" element={<PMMFAVerify />} />
                <Route element={<PMLayout />}>
                  <Route index element={<PMDashboard />} />
                  <Route path="command-center" element={<PMCommandCenterPage />} />
                  <Route path="end-users" element={<PMEndUsersPage />} />
                  <Route path="end-users/:endUserId" element={<PMEndUserDetailPage />} />
                  <Route path="end-users/webhooks/audit" element={<PMWebhookAuditPage />} />
                  <Route path="onboarding-status" element={<PMOnboardingStatusPage />} />
                  <Route path="cards" element={<PMCardsPage />} />
                  <Route path="cards/:programCardId" element={<PMCardDetailPage />} />
                  <Route path="orders" element={<PMOrdersPage />} />
                  <Route path="transactions" element={<PMTransactionsPage />} />
                  <Route path="disputes" element={<PMDisputesPage />} />
                  <Route path="fraud" element={<PMFraudAlertsPage />} />
                  <Route path="controls" element={<PMControlsPage />} />
                  <Route path="reports" element={<PMReportsPage />} />
                  <Route path="compliance" element={<PMCompliancePage />} />
                  <Route path="team" element={<PMTeamPage />} />
                  <Route path="api" element={<PMApiPage />} />
                  <Route path="billing" element={<PMBillingPage />} />
                  <Route path="audit" element={<PMAuditPage />} />
                  <Route path="approvals" element={<PMApprovalsPage />} />
                  <Route path="scheduled-reports" element={<PMScheduledReportsPage />} />
                  <Route path="analytics" element={<PMAnalyticsPage />} />
                  <Route path="requests" element={<PMRequestsPage />} />
                  <Route path="settings" element={<PMSettingsPage />} />
                  <Route path="settings/branding" element={<PMBrandingPage />} />
                  <Route path="settings/webhooks" element={<PMWebhooksPage />} />
                  <Route path="settlement" element={<PMSettlementPage />} />
                  <Route path="statements" element={<PMStatementsPage />} />
                  <Route path="access-denied" element={<PMAccessDenied />} />
                </Route>
              </Route>

              {/* BaaS Partner Portal — Separate app shell at /baas/* */}
              <Route path="/baas" element={<BaaSAuthLayout />}>
                <Route path="login" element={<BaaSLogin />} />
                <Route path="set-password" element={<BaaSSetPassword />} />
                <Route path="mfa-setup" element={<BaaSMFASetup />} />
                <Route path="mfa-verify" element={<BaaSMFAVerify />} />
                <Route element={<BaaSLayout />}>
                  <Route index element={<BaaSDashboard />} />
                  <Route path="customers" element={<BaaSCustomersPage />} />
                  <Route path="customers/new" element={<BaaSNewCustomerPage />} />
                  <Route path="kyc" element={<BaaSKYCPage />} />
                  <Route path="accounts" element={<BaaSAccountsPage />} />
                  <Route path="alerts" element={<BaaSAlertsPage />} />
                  <Route path="exports" element={<BaaSExportsPage />} />
                  <Route path="audit" element={<BaaSAuditPage />} />
                  <Route path="settings" element={<BaaSSettingsPage />} />
                  <Route path="manual-ops" element={<BaaSManualOpsPage />} />
                  <Route path="sandbox" element={<BaaSSandboxPage />} />
                  <Route path="status" element={<BaaSStatusPage />} />
                  <Route path="safeguarding" element={<BaaSSafeguardingPage />} />
                  <Route path="support" element={<BaaSSupportPage />} />
                  <Route path="billing" element={<BaaSBillingPage />} />
                  <Route path="payments" element={<BaaSPaymentsPage />} />
                  <Route path="requests" element={<BaaSRequestsPage />} />
                  <Route path="cases" element={<BaaSCasesPage />} />
                </Route>
              </Route>

              {/* Public standalone identity verification pages */}
              <Route path="/verify" element={<VerifyIdentityPage />} />
              <Route path="/pm/verify" element={<PMVerifyEndUser />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </ErrorBoundary>
  </QueryClientProvider>
);

export default App;
